import { useContext, forwardRef } from 'react';
import { Link, NavLink, withRouter } from 'react-router-dom';

// Externals
import classNames from 'classnames';

// Authentication provider & Firebase
import { AuthContext } from 'services/auth';

// Shared helpers
import { getInitials } from 'helpers';

// Material components
import {
  // Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  // CircularProgress,
  Chip,
  withStyles
} from '@material-ui/core';

// Info icon
import {
  InfoRounded as InfoIcon,
  BugReportRounded as BugIcon,
  QuestionAnswerRounded as FAQ
} from '@mui/icons-material';

// Material UI lab
import { Skeleton } from '@material-ui/lab';

// Sidebar items
import getSidebarItems from 'services/views';

// Cleanup exports
import compose from 'recompose/compose';

// App config
import AppConfig from 'config';

// Component styles
import styles from './styles';
import { DateTime } from 'luxon';

function Sidebar(props) {
  // Grab values out of the component's properties
  const {
    classes,
    className,
    itemClicked,
    profile,
    profileError // ,
    // onProfileRetry
  } = props;

  const auth = useContext(AuthContext);

  const { displayName, photoURL } = auth.user;

  // Get the root classname
  const rootClassName = classNames(classes.root, className);

  // Create a ref component for the sidebar links
  // eslint-disable-next-line react/display-name
  const SidebarNavLink = forwardRef((newProps, ref) => (
    <NavLink onClick={itemClicked} innerRef={ref} {...newProps} />
  ));

  return (
    <nav className={rootClassName}>
      <div className={classes.logoWrapper}>
        <Link className={classes.logoLink} to={`/${auth.role}/dashboard`}>
          <div className={classes.logoContent}>
            <img
              width="55px"
              height="55px"
              alt="e-English logo"
              className={classes.logoImage}
              src="/images/logos/new-logo.png"
            />
            <div className={classes.logoText}>
              <Typography variant="h3" className={classes.logoTitle}>
                e-English
              </Typography>
              <Typography variant="caption" className={classes.logoSubtitle}>
                English Online Tutoring
              </Typography>
            </div>
          </div>
        </Link>
      </div>
      <Divider className={classes.logoDivider} />
      <div className={classes.profile}>
        <Avatar alt={displayName} className={classes.avatar} src={photoURL}>
          <Typography variant="h3" style={{ color: 'white' }}>
            {getInitials(displayName && displayName)}
          </Typography>
        </Avatar>
        <div className={classes.profileDetails}>
          <Typography className={classes.nameText} variant="h5">
            {displayName}
          </Typography>
          <Typography className={classes.bioText} variant="caption">
            {
              {
                student: 'Student',
                tutor: 'Teacher',
                admin: 'Administrator'
              }[auth.role]
            }
          </Typography>
        </div>
      </div>
      <Divider className={classes.profileDivider} />
      {(() => {
        // Make sure we're a student
        if (auth.role === 'student') {
          if (profileError) {
            return (
              <div className={classes.tokenInfo}>
                <Typography variant="h6">{profileError}</Typography>
                <Divider className={classes.profileDivider} />
              </div>
            );
          }

          if (!profile) {
            return (
              <>
                <div className={classes.tokenInfo}>
                  <Skeleton className={classes.skeletonChip}>
                    <Chip size="small" label="10 Lesson Tokens" />
                  </Skeleton>
                  <Skeleton
                    className={`${classes.skeletonChip} ${classes.subBadge}`}>
                    <Chip size="small" label="No Subscription" />
                  </Skeleton>
                </div>
                <Divider className={classes.profileDivider} />
              </>
            );
          }

          if (profile) {
            const sub = profile.stripe.subscription;
            const freeTrialActivated = profile.stripe.freeTrialActivated;
            const freeTrialToken = profile.data?.freeTrialToken || 0;
            const isFreeTrialActive = freeTrialActivated && freeTrialToken > 0;
            console.log({ profile });
            // Mapping of price IDs to their corresponding categories
            const subscriptionCategories = {
              oneOnOneWorkPlace: [
                'price_1RyXvuHns6zXPgc8RhXl2XKb',
                'price_1S2SmBHns6zXPgc8biFSc3Hz',
                'price_1SxjmmHns6zXPgc8eMMrbiHP'
              ],
              oneOnOnePte: [
                'price_1ScelVHns6zXPgc8TXmCFOwx',
                'price_1Sg0VXHns6zXPgc8UdS7MI03'
              ],
              groupWorkPlace: [
                'price_1RyYKKHns6zXPgc8zJMUGqcV',
                'price_1SxjnfHns6zXPgc8OLOGyEl6'
              ],
              groupPte: [
                'price_1ScemtHns6zXPgc8zPNtTblS',
                'price_1Sg0wAHns6zXPgc80aq9855D',
                'price_1S7sUgHns6zXPgc861vlNgvc'
              ]
            };
            // const subscriptionCategories = {
            //   oneOnOneWorkPlace: ['price_1RyXvuHns6zXPgc8RhXl2XKb'],
            //   oneOnOnePte: [
            //     'price_1Sg0VXHns6zXPgc8UdS7MI03',
            //     'price_1S0HiQHns6zXPgc8NjgVQkpi',
            //     'price_1RyWeAHns6zXPgc8zM6LN9LF',
            //     'price_1R4HRwHns6zXPgc8A2KYJiq8',
            //     'price_1R4HKZHns6zXPgc866zuYJzK'
            //   ],
            //   groupWorkPlace: ['price_1RyYKKHns6zXPgc8zJMUGqcV'],
            //   groupPte: [
            //     'price_1Sg0wAHns6zXPgc80aq9855D',
            //     'price_1S7sUgHns6zXPgc861vlNgvc',
            //     'price_1RyYEwHns6zXPgc8cDIB4lW7',
            //     'price_1R4IerHns6zXPgc8RBjX0DfX',
            //     'price_1R4He5Hns6zXPgc8fCOkH7Mt'
            //   ]
            // };

            // Find subscriptions based on price IDs
            const findSubscription = (priceIds) =>
              sub?.currentSubscription?.categories?.find((f) =>
                Array.isArray(priceIds)
                  ? priceIds.includes(f.priceId)
                  : f.priceId === priceIds
              );

            // const findSubscription = (priceIds) =>
            //   sub?.currentSubscription?.categories?.find((f) =>
            //     priceIds.includes(f.priceId)
            //   );

            // Render subscription section
            const renderSubscriptionSection = (title, subscriptions) => {
              const validSubscriptions = subscriptions.filter(
                (sub) => sub.category
              );
              console.log(title, validSubscriptions, { subscriptions });

              if (validSubscriptions.length === 0) return null;

              return (
                <div
                  className={classes.subscription}
                  style={title === 'PTE English' ? { marginTop: '10px' } : {}}>
                  <div className={classes.subscriptionTitle}>
                    <Typography variant="h5">{title}</Typography>
                  </div>
                  {validSubscriptions.map((sub, index) => (
                    <>
                      {index > 0 && <Divider className={classes.subdevider} />}
                      <Typography
                        className={classes.subscription_name}
                        variant="h6">
                        {profile.data[sub.tokenKey] || 0} {sub.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Expires{' '}
                        {sub.category?.expiredAt &&
                          DateTime.fromMillis(
                            // Handle both seconds and milliseconds timestamps
                            sub.category.expiredAt < 10000000000
                              ? sub.category.expiredAt * 1000
                              : sub.category.expiredAt
                          ).toFormat('dd LLLL yyyy')}
                      </Typography>
                    </>
                  ))}
                </div>
              );
            };
            return (
              <>
                <div className={classes.tokenInfo}>
                  {sub?.currentSubscription &&
                  sub?.currentSubscription?.categories?.length > 0 ? (
                    <>
                      {renderSubscriptionSection('Workplace English', [
                        {
                          category: findSubscription(
                            subscriptionCategories.oneOnOneWorkPlace
                          ),
                          tokenKey: 'workplaceLessonTokens',
                          label: 'One-on-One Lessons'
                        },
                        {
                          category: findSubscription(
                            subscriptionCategories.groupWorkPlace
                          ),
                          tokenKey: 'workplaceGroupLessonTokens',
                          label: 'Group Sessions'
                        }
                      ])}

                      {renderSubscriptionSection('PTE English', [
                        // {
                        //   category: findSubscription(
                        //     subscriptionCategories.oneOnOnePteA2
                        //   ),
                        //   tokenKey: 'A2PTELessonTokens',
                        //   label: 'One-on-One PTE A2 Lessons'
                        // },
                        {
                          category: findSubscription(
                            subscriptionCategories.oneOnOnePte
                          ),
                          tokenKey: 'PTELessonTokens',
                          label: 'One-on-One PTE Lessons'
                        },
                        // {
                        //   category: findSubscription(
                        //     subscriptionCategories.oneOnOnePteB2
                        //   ),
                        //   tokenKey: 'B2PTELessonTokens',
                        //   label: 'One-on-One PTE B2 Lessons'
                        // },
                        // {
                        //   category: findSubscription(
                        //     subscriptionCategories.groupPteA2
                        //   ),
                        //   tokenKey: 'A2PTEGroupLessonTokens',
                        //   label: 'Group Sessions PTE A2'
                        // },
                        {
                          category: findSubscription(
                            subscriptionCategories.groupPte
                          ),
                          tokenKey: 'PTEGroupLessonTokens',
                          label: 'Group Sessions PTE'
                        }
                        // {
                        //   category: findSubscription(
                        //     subscriptionCategories.groupPteB2
                        //   ),
                        //   tokenKey: 'B2PTEGroupLessonTokens',
                        //   label: 'Group Sessions PTE B2'
                        // }
                      ])}
                    </>
                  ) : freeTrialActivated &&
                    (profile.data?.freeTrialToken || 0) > 0 ? (
                    <div className={classes.subscription}>
                      <div className={classes.subscriptionTitle}>
                        <Typography variant="h5">Free Trial</Typography>
                      </div>
                      <Typography
                        className={classes.subscription_name}
                        variant="h6">
                        {profile.data?.freeTrialToken || 0} Free Trial Lessons
                      </Typography>
                    </div>
                  ) : (
                    <Chip
                      size="small"
                      className={`${classes.infoChip}`}
                      label="No Subscription"
                    />
                  )}
                </div>
                <Divider className={classes.profileDivider} />
              </>
            );
          }
        }

        return null;
      })()}
      <List component="div" disablePadding>
        {getSidebarItems(auth.role, auth.isDevBuild).map((item) => {
          if (item.icon !== undefined && item.text !== undefined) {
            return (
              <ListItem
                activeClassName={classes.activeListItem}
                className={classes.listItem}
                component={SidebarNavLink}
                to={item.route}
                key={item.route}>
                <ListItemIcon className={classes.listItemIcon}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  classes={{ primary: classes.listItemText }}
                  primary={item.text}
                />
              </ListItem>
            );
          }
          return null;
        })}
      </List>
      <Divider className={classes.listDivider} />
      <List
        component="div"
        disablePadding
        subheader={
          <ListSubheader className={classes.listSubheader}>
            Support
          </ListSubheader>
        }>
        <ListItem
          className={classes.listItem}
          component="a"
          href="mailto:support@e-english.com.au"
          target="_blank">
          <ListItemIcon className={classes.listItemIcon}>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText
            classes={{ primary: classes.listItemText }}
            primary="Contact Us"
          />
        </ListItem>
        {auth.role !== 'admin' && (
          <ListItem
            className={classes.listItem}
            component="a"
            href={
              auth.role === 'tutor'
                ? 'https://docs.google.com/forms/d/e/1FAIpQLSc1abmrnw9R9l4-QqAUynwxwlPi2CCKhJHQjliy3luOoCZbsA/viewform?usp=sharing'
                : 'https://docs.google.com/forms/d/e/1FAIpQLSextGBWDBn1kgZWaKXG88eskrDceITjK5ryqbqCl2QNTjtTUQ/viewform?usp=sharing'
            }
            target="_blank">
            <ListItemIcon className={classes.listItemIcon}>
              <BugIcon />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.listItemText }}
              primary="Submit feedback"
            />
          </ListItem>
        )}
        {auth.role == 'tutor' && (
          <ListItem
            className={classes.listItem}
            component="a"
            href={
              'https://firebasestorage.googleapis.com/v0/b/eng-tutor-app.appspot.com/o/tutorfaqs%2FFAQ%20TUTOR_v1.pdf?alt=media&token=b3d16971-ec0d-4878-a8cb-3397843698ac'
            }
            target="_blank">
            <ListItemIcon className={classes.listItemIcon}>
              <FAQ />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.listItemText }}
              primary="FAQ"
            />
          </ListItem>
        )}
      </List>
      <Divider className={classes.listDivider} />
      <ListSubheader className={classes.listVersionSubheader}>
        Build ID: {AppConfig.buildId}
      </ListSubheader>
    </nav>
  );
}

export default compose(withRouter, withStyles(styles))(Sidebar);
